import { createActuatorControlPayload } from "../../physics/actuatorMapping";
import { getPhysicsWorker } from "../../physics/workerHandle";
import { getSceneState } from "../../stores/sceneStore";
import { usePhysicsStore } from "../../stores/physicsStore";
import type { Equality, Joint } from "../../types/scene";
import { getScalarJointRange } from "./jointUtils";
import type { JointRecord, TendonAwareActuator } from "./types";

function buildRobustTendonControlPayload(
  actuatorList: TendonAwareActuator[],
  tendonAct: TendonAwareActuator,
  key: string,
  value: number
) {
  const basePayload = createActuatorControlPayload({ [key]: value }, actuatorList);
  const tendonNameKey = tendonAct?.tendon ? String(tendonAct.tendon) : "";
  const payload: Record<string, number> = { ...basePayload };
  if (tendonNameKey) payload[tendonNameKey] = value;
  if (Object.keys(basePayload).length === 0) {
    payload[key] = value;
    const nameKey = tendonAct?.name ? String(tendonAct.name) : "";
    if (nameKey) payload[nameKey] = value;
  }
  return payload;
}

function inferTendonLowMeansClose(
  actuator: TendonAwareActuator,
  actuatorList: TendonAwareActuator[]
): boolean {
  const actuatorName = String(actuator?.name || "").toLowerCase();
  const tendonName = String(actuator?.tendon || "").toLowerCase();
  if (
    actuatorName === "actuator8" ||
    actuatorName.includes("panda") ||
    tendonName.includes("panda")
  ) {
    return true;
  }
  if (actuatorName.includes("fingers_actuator") || actuatorName.includes("2f85")) {
    return false;
  }
  try {
    const s = getSceneState();
    const joints = (s?.joints || {}) as JointRecord;
    const equalities = Object.values(s?.equality || {}) as Equality[];
    const tendonActuators = (actuatorList || []).filter((a) => !!a?.tendon);
    const tendonIdx = tendonActuators.findIndex(
      (a) =>
        a === actuator ||
        (!!a?.uuid && !!actuator?.uuid && a.uuid === actuator.uuid) ||
        (a?.name === actuator?.name && a?.tendon === actuator?.tendon)
    );
    const tendonJointEqualities = equalities.filter(
      (eq): eq is Equality & { obj1: string; obj2: string } =>
        !!eq &&
        eq.type === "joint" &&
        (eq.objtype === "joint" || !eq.objtype) &&
        typeof eq.obj1 === "string" &&
        typeof eq.obj2 === "string" &&
        !!joints[eq.obj1] &&
        !!joints[eq.obj2]
    );
    const matchedEq =
      tendonIdx >= 0 && tendonIdx < tendonJointEqualities.length
        ? tendonJointEqualities[tendonIdx]
        : null;
    const jointNames = matchedEq
      ? [joints[matchedEq.obj1]?.name, joints[matchedEq.obj2]?.name]
          .filter(Boolean)
          .map((n) => String(n).toLowerCase())
      : [];
    if (jointNames.some((n: string) => n.includes("finger_joint"))) return true;
    if (jointNames.some((n: string) => n.includes("driver_joint"))) return false;
  } catch {
    /* ignore */
  }
  return false;
}

function resolveTendonGripperControl(
  actuator: TendonAwareActuator,
  actuatorList: TendonAwareActuator[]
): { open: number; close: number } {
  const lo = Number(Array.isArray(actuator?.ctrlrange) ? actuator.ctrlrange[0] : 0);
  const hi = Number(Array.isArray(actuator?.ctrlrange) ? actuator.ctrlrange[1] : 255);
  const ctrlLo = Number.isFinite(lo) ? lo : 0;
  const ctrlHi = Number.isFinite(hi) && hi > ctrlLo ? hi : 255;
  const lowMeansClose = inferTendonLowMeansClose(actuator, actuatorList);
  return lowMeansClose ? { open: ctrlHi, close: ctrlLo } : { open: ctrlLo, close: ctrlHi };
}

function findGripperAct(
  actuatorList: TendonAwareActuator[]
): { act: TendonAwareActuator; isTendon: boolean } | null {
  const byName = (a: TendonAwareActuator) => String(a?.name || "").toLowerCase();
  const byJoint = (a: TendonAwareActuator) => String(a?.joint || "").toLowerCase();
  const isGripperish = (s: string) => s.includes("finger") || s.includes("gripper");
  const t1 = actuatorList.find((a) => a?.tendon && byName(a).includes("fingers_actuator"));
  if (t1) return { act: t1, isTendon: true };
  const t2 = actuatorList.find((a) => a?.tendon);
  if (t2) return { act: t2, isTendon: true };
  const nonTendon =
    actuatorList.find(
      (a) => !a?.tendon && a?.joint && (isGripperish(byJoint(a)) || isGripperish(byName(a)))
    ) || actuatorList.find((a) => !a?.tendon && !a?.joint && isGripperish(byName(a)));
  if (nonTendon) return { act: nonTendon, isTendon: false };
  return null;
}

function resolveGripperControl(
  act: TendonAwareActuator,
  isTendon: boolean,
  actuatorList: TendonAwareActuator[]
): { open: number; close: number } {
  if (isTendon) return resolveTendonGripperControl(act, actuatorList);
  const ctrlrange = Array.isArray(act?.ctrlrange) ? act.ctrlrange : null;
  if (ctrlrange && ctrlrange.length >= 2) {
    const lo = Number(ctrlrange[0]);
    const hi = Number(ctrlrange[1]);
    if (Number.isFinite(lo) && Number.isFinite(hi) && hi > lo) return { open: hi, close: lo };
  }
  try {
    const s = getSceneState();
    const joints = Object.values(s?.joints || {}) as Joint[];
    const jointName = String(act?.joint || "");
    const joint = joints.find((j) => j?.name === jointName || j?.uuid === jointName);
    const jrange = getScalarJointRange(joint);
    if (jrange && jrange.length >= 2) {
      const lo = Number(jrange[0]);
      const hi = Number(jrange[1]);
      if (Number.isFinite(lo) && Number.isFinite(hi) && hi > lo) return { open: hi, close: lo };
    }
  } catch {
    /* ignore */
  }
  return { open: 1, close: 0 };
}

export type GripperPostResult =
  | { ok: true; key: string; value: number }
  | { ok: false; reason?: string };

/** Send open/close gripper controls to the physics worker (same logic as pick/place servo). */
export function postGripperControl(
  mode: "open" | "close",
  workerOverride?: unknown
): GripperPostResult {
  const physicsState = usePhysicsStore.getState();
  const physicsWorker = (workerOverride ?? getPhysicsWorker()) as {
    postMessage: (message: unknown) => void;
  } | null;
  if (physicsState.isRunning !== true || !physicsWorker) {
    return { ok: false, reason: "physics_not_running" };
  }
  const s = getSceneState();
  const actuatorList = Object.values(s.actuators || {}) as TendonAwareActuator[];
  const found = findGripperAct(actuatorList);
  if (!found) return { ok: false, reason: "no_gripper_actuator" };
  const { act, isTendon } = found;
  const key = act.uuid || act.name;
  if (!key) return { ok: false, reason: "no_actuator_key" };
  const ctrl = resolveGripperControl(act, isTendon, actuatorList);
  const value = mode === "close" ? ctrl.close : ctrl.open;
  const payload = isTendon
    ? buildRobustTendonControlPayload(actuatorList, act, String(key), value)
    : createActuatorControlPayload({ [String(key)]: value }, actuatorList);
  if (Object.keys(payload).length === 0) return { ok: false, reason: "empty_payload" };
  try {
    physicsWorker.postMessage({ type: "controls", payload });
    return { ok: true, key: String(key), value };
  } catch {
    return { ok: false, reason: "post_failed" };
  }
}
